/**
 * useMonthlyBudgetOverride Hook
 *
 * 특정 월에 대한 예산 오버라이드를 관리하는 Hook
 *
 * 책임:
 * - 템플릿 예산 로드 (year: null, month: null)
 * - 특정 월 예산 로드 (year: X, month: Y)
 * - 카테고리별 오버라이드 토글 상태 관리
 * - 예산 저장/삭제 (오버라이드 ON/OFF에 따라)
 * - 템플릿으로 초기화 (해당 월의 모든 오버라이드 삭제)
 *
 * 우선순위 로직:
 * - Repository에서 자동 처리 (priority 3: 특정 year+month > priority 1: template)
 * - UI에서는 템플릿과 월별 예산을 별도로 로드하여 비교 표시
 */

import { useState, useEffect, useCallback } from 'react'
import { budgetRepository } from '../repositories'
import { cascadeDeleteBudget } from '../utils/budgetHelpers'
import type { Budget } from '../types/Budget'

/**
 * useMonthlyBudgetOverride Hook 반환 타입
 */
export interface UseMonthlyBudgetOverrideReturn {
  /** 템플릿 예산 목록 (year: null, month: null) */
  templateBudgets: Budget[]
  /** 월별 오버라이드 예산 목록 (year: X, month: Y) */
  monthlyBudgets: Budget[]
  /** 카테고리별 오버라이드 상태 (categoryId -> boolean) */
  overrideStates: Record<string, boolean>
  /** 카테고리별 금액 (categoryId -> amount) */
  budgetAmounts: Record<string, number>

  /** 오버라이드 토글 (템플릿 사용 ↔ 월별 예산 설정) */
  toggleOverride: (categoryId: string) => void
  /** 예산 금액 업데이트 */
  updateBudgetAmount: (categoryId: string, amount: number) => void
  /** 템플릿으로 초기화 (해당 월의 모든 오버라이드 삭제) */
  resetToTemplate: () => Promise<void>
  /** 예산 저장 */
  saveBudgets: () => Promise<void>

  /** 로딩 상태 (초기 로드) */
  loading: boolean
  /** 저장 중 상태 */
  saving: boolean
  /** 에러 정보 */
  error: Error | null
}

/**
 * 특정 월의 예산 오버라이드를 관리하는 Hook
 *
 * @param year 대상 연도 (예: 2025)
 * @param month 대상 월 (1-12)
 * @returns 템플릿 예산, 월별 예산, 상태, 조작 함수
 *
 * @example
 * ```typescript
 * const {
 *   templateBudgets,
 *   monthlyBudgets,
 *   overrideStates,
 *   toggleOverride,
 *   saveBudgets
 * } = useMonthlyBudgetOverride(2025, 12)
 * ```
 */
export function useMonthlyBudgetOverride(
  year: number,
  month: number
): UseMonthlyBudgetOverrideReturn {
  // 데이터 상태
  const [templateBudgets, setTemplateBudgets] = useState<Budget[]>([])
  const [monthlyBudgets, setMonthlyBudgets] = useState<Budget[]>([])

  // UI 상태 (사용자 입력)
  const [overrideStates, setOverrideStates] = useState<Record<string, boolean>>({})
  const [budgetAmounts, setBudgetAmounts] = useState<Record<string, number>>({})

  // 로딩/에러 상태
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<Error | null>(null)

  /**
   * 예산 데이터 로드
   *
   * 로직:
   * 1. 템플릿 예산 조회 (year: null, month: null)
   * 2. 해당 월 예산 조회 (year: X, month: Y)
   * 3. UI 상태 초기화
   *    - overrideStates: 월별 예산이 있으면 true
   *    - budgetAmounts: 월별 예산이 있으면 월별 금액, 없으면 템플릿 금액
   */
  const loadBudgets = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)

      // 1. 템플릿 예산 조회
      const templates = await budgetRepository.findAll({
        year: null,
        month: null,
        isActive: true,
      })

      // 2. 해당 월 예산 조회
      const monthly = await budgetRepository.findAll({
        year,
        month,
        isActive: true,
      })

      setTemplateBudgets(templates)
      setMonthlyBudgets(monthly)

      // 3. UI 상태 초기화
      const newOverrideStates: Record<string, boolean> = {}
      const newBudgetAmounts: Record<string, number> = {}

      // 템플릿을 기본으로 설정
      for (const template of templates) {
        newOverrideStates[template.categoryId] = false
        newBudgetAmounts[template.categoryId] = template.amount
      }

      // 월별 예산이 있으면 오버라이드
      for (const monthlyBudget of monthly) {
        newOverrideStates[monthlyBudget.categoryId] = true
        newBudgetAmounts[monthlyBudget.categoryId] = monthlyBudget.amount
      }

      setOverrideStates(newOverrideStates)
      setBudgetAmounts(newBudgetAmounts)
    } catch (err) {
      setError(
        err instanceof Error
          ? err
          : new Error('예산 목록을 불러오는데 실패했습니다')
      )
    } finally {
      setLoading(false)
    }
  }, [year, month])

  /**
   * 초기 로드
   */
  useEffect(() => {
    loadBudgets()
  }, [loadBudgets])

  /**
   * 오버라이드 토글
   *
   * @param categoryId 카테고리 ID
   *
   * 동작:
   * - OFF → ON: 템플릿 금액을 초기값으로 복사
   * - ON → OFF: 템플릿 금액으로 되돌림
   */
  const toggleOverride = useCallback(
    (categoryId: string) => {
      setOverrideStates(prev => {
        const newState = !prev[categoryId]
        return { ...prev, [categoryId]: newState }
      })

      // 토글 시 금액 초기화 (템플릿 값으로)
      if (!overrideStates[categoryId]) {
        // OFF → ON: 템플릿 금액 복사
        const template = templateBudgets.find(b => b.categoryId === categoryId)
        if (template) {
          setBudgetAmounts(prev => ({
            ...prev,
            [categoryId]: template.amount,
          }))
        }
      } else {
        // ON → OFF: 템플릿 금액으로 되돌림
        const template = templateBudgets.find(b => b.categoryId === categoryId)
        if (template) {
          setBudgetAmounts(prev => ({
            ...prev,
            [categoryId]: template.amount,
          }))
        }
      }
    },
    [overrideStates, templateBudgets]
  )

  /**
   * 예산 금액 업데이트
   *
   * @param categoryId 카테고리 ID
   * @param amount 금액
   */
  const updateBudgetAmount = useCallback(
    (categoryId: string, amount: number) => {
      setBudgetAmounts(prev => ({
        ...prev,
        [categoryId]: amount,
      }))
    },
    []
  )

  /**
   * 예산 저장
   *
   * 로직:
   * 1. overrideStates를 순회
   * 2. true (오버라이드 ON):
   *    - 기존 월별 예산이 있으면 update
   *    - 없으면 create (year: X, month: Y)
   * 3. false (오버라이드 OFF):
   *    - 기존 월별 예산이 있으면 delete (템플릿으로 폴백)
   *
   * 주의:
   * - 대분류-소분류 관계는 Phase 1에서는 독립적으로 처리
   * - parentBudgetId는 사용하지 않음 (향후 Phase 2에서 개선)
   */
  const saveBudgets = useCallback(async () => {
    try {
      setSaving(true)
      setError(null)

      console.log('=== 월별 예산 저장 시작 ===')
      console.log('year:', year, 'month:', month)
      console.log('overrideStates:', overrideStates)
      console.log('budgetAmounts:', budgetAmounts)

      for (const [categoryId, isOverride] of Object.entries(overrideStates)) {
        const amount = budgetAmounts[categoryId]
        const existingMonthly = monthlyBudgets.find(
          b => b.categoryId === categoryId
        )

        if (isOverride) {
          // 오버라이드 ON: 월별 예산 생성/수정
          // amount가 undefined이거나 0 이하면 스킵
          if (amount === undefined || amount <= 0) {
            console.log(`⏭️  스킵: ${categoryId} (amount=${amount})`)
            continue
          }

          if (existingMonthly) {
            // 기존 월별 예산 업데이트
            console.log(`✏️  업데이트: ${categoryId} = ${amount}원`)
            await budgetRepository.update(existingMonthly.id, {
              amount,
            })
          } else {
            // 새 월별 예산 생성
            console.log(`➕ 생성: ${categoryId} = ${amount}원`)
            await budgetRepository.create({
              categoryId,
              amount,
              year,
              month,
              isActive: true,
            })
          }
        } else {
          // 오버라이드 OFF: 월별 예산 삭제 (템플릿으로 폴백)
          if (existingMonthly) {
            console.log(`🗑️  삭제: ${categoryId} (템플릿으로 폴백)`)

            // 대분류면 cascade delete, 소분류면 일반 delete
            if (!existingMonthly.parentBudgetId) {
              const deletedIds = await cascadeDeleteBudget(budgetRepository, existingMonthly.id)
              console.log(`  └─ cascade: ${deletedIds.length}개 예산 삭제`)
            } else {
              await budgetRepository.delete(existingMonthly.id)
            }
          }
        }
      }

      console.log('✅ 저장 완료')

      // 데이터 재로드
      await loadBudgets()
    } catch (err) {
      setError(
        err instanceof Error ? err : new Error('예산 저장에 실패했습니다')
      )
      throw err
    } finally {
      setSaving(false)
    }
  }, [
    year,
    month,
    overrideStates,
    budgetAmounts,
    monthlyBudgets,
    loadBudgets,
  ])

  /**
   * 템플릿으로 초기화
   *
   * 로직:
   * - 해당 월의 모든 월별 예산 삭제
   * - 데이터 재로드 (템플릿이 표시됨)
   */
  const resetToTemplate = useCallback(async () => {
    try {
      setSaving(true)
      setError(null)

      console.log(`=== 템플릿으로 초기화: ${year}년 ${month}월 ===`)

      // 해당 월의 모든 월별 예산 삭제 (cascade delete 적용)
      for (const monthlyBudget of monthlyBudgets) {
        console.log(`🗑️  삭제: ${monthlyBudget.categoryId}`)

        // 대분류면 cascade delete, 소분류면 일반 delete
        if (!monthlyBudget.parentBudgetId) {
          const deletedIds = await cascadeDeleteBudget(budgetRepository, monthlyBudget.id)
          console.log(`  └─ cascade: ${deletedIds.length}개 예산 삭제`)
        } else {
          await budgetRepository.delete(monthlyBudget.id)
        }
      }

      console.log('✅ 초기화 완료')

      // 데이터 재로드
      await loadBudgets()
    } catch (err) {
      setError(
        err instanceof Error
          ? err
          : new Error('템플릿 초기화에 실패했습니다')
      )
      throw err
    } finally {
      setSaving(false)
    }
  }, [year, month, monthlyBudgets, loadBudgets])

  return {
    templateBudgets,
    monthlyBudgets,
    overrideStates,
    budgetAmounts,
    toggleOverride,
    updateBudgetAmount,
    resetToTemplate,
    saveBudgets,
    loading,
    saving,
    error,
  }
}
